import React, { useState, useEffect } from "react";
import { Instagram, Heart, MessageCircle, Send as SendIcon, Bookmark, Grid3x3, ExternalLink, Loader2, RefreshCw } from "lucide-react";
import { getScheduledPosts, getMedia, fetchProperties, getDrafts, getInstagramFeed } from "../../api";

const num = (n) => (n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : `${n || 0}`);

const SocialPreview = () => {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(0);
  const [live, setLive] = useState(null);      // {connected, profile, posts} or null
  const [loading, setLoading] = useState(true);

  const loadLive = async () => {
    setLoading(true);
    try {
      const f = await getInstagramFeed();
      if (f?.connected && (f.posts || []).length) {
        setLive(f);
        setItems(f.posts.map((p) => ({ image: p.image, caption: p.caption, when: p.timestamp ? new Date(p.timestamp).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "", likes: p.likes, comments: p.comments, permalink: p.permalink })));
        setLoading(false);
        return;
      }
      setLive(f || { connected: false });
    } catch { setLive({ connected: false }); }
    // fallback preview built from scheduled posts / drafts / media
    const [posts, media, drafts, props] = await Promise.all([
      getScheduledPosts().catch(() => []), getMedia().catch(() => []),
      getDrafts({ type: "post" }).catch(() => []), fetchProperties({}).catch(() => []),
    ]);
    const built = [];
    posts.forEach((p) => p.image && built.push({ image: p.image, caption: p.caption, when: p.bestTime || "Scheduled" }));
    const captions = drafts.map((d) => d.content).filter(Boolean);
    props.forEach((pr, i) => {
      const img = (pr.images || [])[0];
      if (img && !built.find((b) => b.image === img)) built.push({ image: img, caption: captions[i % (captions.length || 1)] || `${pr.title} — now available with Anadear Estates.`, when: "Draft preview" });
    });
    media.forEach((m) => m.url && built.push({ image: m.url, caption: m.name || "Anadear Estates", when: "Media" }));
    setItems(built.slice(0, 12));
    setLoading(false);
  };

  useEffect(() => { loadLive(); }, []);

  if (loading) return <div className="bg-white rounded-2xl border border-[#f0ece5] p-10 text-center text-[#8a8a8a] flex items-center justify-center gap-2" data-testid="social-preview"><Loader2 size={16} className="animate-spin" /> Loading your Instagram feed…</div>;

  const connected = live?.connected;
  const profile = live?.profile;

  if (items.length === 0) return (
    <div className="bg-white rounded-2xl border border-[#f0ece5] p-8 text-center text-[#8a8a8a]" data-testid="social-preview">
      {connected ? "Your Instagram is connected but has no posts yet." : (live?.error ? `Instagram not connected: ${live.error}` : "Connect Instagram in Connections, or add media / schedule a post to see a preview.")}
    </div>
  );
  const cur = items[active] || items[0];

  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-6" data-testid="social-preview">
      <div className="bg-white rounded-2xl border border-[#f0ece5] p-6">
        <div className="flex items-center justify-between mb-1">
          <span className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full ${connected ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`} data-testid="ig-status">
            <span className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-green-500" : "bg-amber-500"}`} /> {connected ? "Live Instagram" : "Preview (not connected)"}
          </span>
          <button onClick={loadLive} className="text-[#8a8a8a] hover:text-[#b8935f] p-1.5" title="Refresh" data-testid="ig-refresh"><RefreshCw size={15} /></button>
        </div>
        <div className="flex items-center gap-4 pb-5 border-b border-[#f0ece5]">
          <div className="w-16 h-16 rounded-full bg-gradient-to-tr from-[#b8935f] to-[#d9b98a] p-[2px]">
            {profile?.avatar ? <img src={profile.avatar} alt="" className="w-full h-full rounded-full object-cover" /> : <div className="w-full h-full rounded-full bg-white flex items-center justify-center"><Instagram size={26} className="text-[#b8935f]" /></div>}
          </div>
          <div>
            <p className="text-[16px] font-500 text-[#2b2b2b]">{profile?.username || "anadearestates"}</p>
            <div className="flex gap-4 text-[13px] text-[#6a6a6a] mt-1">
              <span><b className="text-[#2b2b2b]">{num(connected ? profile?.mediaCount : items.length)}</b> posts</span>
              <span><b className="text-[#2b2b2b]">{connected ? num(profile?.followers) : "—"}</b> followers</span>
              <span><b className="text-[#2b2b2b]">{connected ? num(profile?.following) : "—"}</b> following</span>
            </div>
            {(profile?.bio || !connected) && <p className="text-[12px] text-[#8a8a8a] mt-1">{profile?.bio || "Connect Instagram to show your live bio & stats."}</p>}
          </div>
        </div>
        <div className="flex items-center justify-center gap-2 py-3 text-[12px] text-[#b8935f] uppercase tracking-wide border-b border-[#f0ece5]"><Grid3x3 size={14} /> {connected ? "Your latest posts" : "Feed preview"}</div>
        <div className="grid grid-cols-3 gap-1 mt-1">
          {items.map((it, i) => (
            <button key={i} onClick={() => setActive(i)} data-testid="feed-tile" className={`relative aspect-square overflow-hidden group ${active === i ? "ring-2 ring-[#b8935f]" : ""}`}>
              <img src={it.image} alt="" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
              {connected && (
                <span className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-3 text-white opacity-0 group-hover:opacity-100 text-[12px] font-500">
                  <span className="flex items-center gap-1"><Heart size={13} /> {num(it.likes)}</span>
                  <span className="flex items-center gap-1"><MessageCircle size={13} /> {num(it.comments)}</span>
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="mx-auto w-full max-w-[340px] rounded-[28px] border-[6px] border-[#1c1c1e] bg-white overflow-hidden shadow-xl" data-testid="post-preview">
          <div className="flex items-center gap-2 px-3 py-2.5 border-b border-[#f0ece5]">
            <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-[#b8935f] to-[#d9b98a] p-[2px]">
              {profile?.avatar ? <img src={profile.avatar} alt="" className="w-full h-full rounded-full object-cover" /> : <div className="w-full h-full rounded-full bg-white flex items-center justify-center"><Instagram size={15} className="text-[#b8935f]" /></div>}
            </div>
            <span className="text-[13px] font-500 text-[#2b2b2b]">{profile?.username || "anadearestates"}</span>
            {cur.permalink && <a href={cur.permalink} target="_blank" rel="noreferrer" className="ml-auto text-[#8a8a8a] hover:text-[#b8935f]"><ExternalLink size={15} /></a>}
          </div>
          <img src={cur.image} alt="" className="w-full aspect-square object-cover" />
          <div className="px-3 py-2.5">
            <div className="flex items-center gap-4 mb-2">
              <Heart size={22} className="text-[#2b2b2b]" /><MessageCircle size={22} className="text-[#2b2b2b]" /><SendIcon size={20} className="text-[#2b2b2b]" />
              <Bookmark size={22} className="text-[#2b2b2b] ml-auto" />
            </div>
            {connected ? (
              <p className="text-[13px] font-500 text-[#2b2b2b] mb-1">{num(cur.likes)} likes · {num(cur.comments)} comments</p>
            ) : null}
            <p className="text-[13px] text-[#2b2b2b] leading-relaxed whitespace-pre-line line-clamp-6"><b>{profile?.username || "anadearestates"}</b> {cur.caption}</p>
            {cur.when && <p className="text-[11px] text-[#a0a0a0] mt-2 uppercase">{cur.when}</p>}
          </div>
        </div>
        <p className="text-center text-[12px] text-[#a0a0a0] mt-3">{connected ? "This is your live Instagram feed." : "Tap a tile to preview how a post will look."}</p>
      </div>
    </div>
  );
};

export default SocialPreview;
